import {
  getGameHome,
  getGameNav,
  listGames,
  listParamsFromNavNode,
  type GameHome,
  type GameNavNode,
} from '@/features/game/game-api';
import { catalogNavNodes, toOroMxGame, withFallbackCollections } from '@/features/game/oro-mx-catalog';
import type { OroMxGame } from '@/shared/theme/oro-mx-home-data';

export type OroMxHomeCatalog = {
  gamesByNodeKey: Record<string, OroMxGame[]>;
  nodes: GameNavNode[];
};

let cached: OroMxHomeCatalog | null = null;
let inflight: Promise<OroMxHomeCatalog> | null = null;

export const mapGameHome = (home: GameHome): OroMxHomeCatalog => {
  const gamesByNodeKey: Record<string, OroMxGame[]> = {};
  for (const section of home.sections ?? []) {
    gamesByNodeKey[section.key] = (section.items ?? []).map(toOroMxGame);
  }
  return {
    gamesByNodeKey,
    nodes: home.nodes ?? [],
  };
};

export const emptyOroMxHomeCatalog = (): OroMxHomeCatalog => ({
  gamesByNodeKey: {},
  nodes: [],
});

/** 聚合接口未部署时：nav + 各节点一页，不再先拉全量游戏。 */
export const loadOroMxHomeCatalogFallback = async (): Promise<OroMxHomeCatalog> => {
  const nav = await getGameNav();
  const nodes = nav.nodes ?? [];
  const catalog = withFallbackCollections(nodes);
  const gamesByNodeKey: Record<string, OroMxGame[]> = {};
  await Promise.all(
    catalogNavNodes(catalog).map(async (node) => {
      try {
        const page = await listGames({ ...listParamsFromNavNode(node), pageNo: 1, pageSize: 60 });
        gamesByNodeKey[node.key] = page.items.map(toOroMxGame);
      } catch {
        gamesByNodeKey[node.key] = [];
      }
    }),
  );
  return { gamesByNodeKey, nodes };
};

export const loadOroMxHomeCatalog = (): Promise<OroMxHomeCatalog> => {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      cached = mapGameHome(await getGameHome());
      return cached;
    } catch {
      cached = await loadOroMxHomeCatalogFallback();
      return cached;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

export const resetOroMxHomeCatalogForTest = () => {
  cached = null;
  inflight = null;
};
